import { useState, useEffect, useCallback } from "react";
import { 
  Users, 
  Check, 
  X, 
  Ban, 
  RefreshCw,
  Clock,
  CheckCircle2,
  XCircle,
  Shield
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const staffRoles = [
  { value: "owner", label: "Owner/CEO" },
  { value: "co_ceo", label: "Co-CEO" },
  { value: "treasurer", label: "Treasurer/CFO" },
  { value: "secretary", label: "Secretary/Admin" },
  { value: "director", label: "Director/Manager" },
  { value: "videographer", label: "Videographer/Editor" },
  { value: "artist", label: "Artist" }
];

const UserManagement = ({ user }) => {
  const [allUsers, setAllUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showDenyDialog, setShowDenyDialog] = useState(false);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState("artist");
  const [denyReason, setDenyReason] = useState("");

  const fetchUsers = useCallback(async () => {
    try {
      const [allRes, pendingRes, customersRes] = await Promise.all([
        fetch(`${API}/admin/users`, { credentials: "include" }),
        fetch(`${API}/admin/users/pending`, { credentials: "include" }),
        fetch(`${API}/admin/customers`, { credentials: "include" })
      ]);

      if (allRes.ok) {
        const data = await allRes.json();
        setAllUsers(data?.users ?? data ?? []);
      } else {
        setAllUsers([]);
      }
      if (pendingRes.ok) {
        const data = await pendingRes.json();
        setPendingUsers(data?.users ?? data ?? []);
      } else {
        setPendingUsers([]);
      }
      if (customersRes.ok) {
        const data = await customersRes.json();
        setCustomers(data?.customers ?? data ?? []);
      } else {
        setCustomers([]);
      }
    } catch {
      toast.error("Failed to load users");
      setAllUsers([]);
      setPendingUsers([]);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleApprove = async () => {
    if (!selectedUser) return;
    try {
      const response = await fetch(`${API}/admin/users/${selectedUser.user_id}/approve`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ role: selectedRole })
      });

      if (response.ok) {
        toast.success(`${selectedUser.name} approved as ${staffRoles.find(r => r.value === selectedRole)?.label}`);
        setShowApproveDialog(false);
        setSelectedUser(null);
        fetchUsers();
      } else {
        toast.error("Failed to approve user");
      }
    } catch (error) {
      toast.error("Error approving user");
    }
  };

  const handleDeny = async () => {
    if (!selectedUser) return;
    try {
      const response = await fetch(`${API}/admin/users/${selectedUser.user_id}/deny`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ reason: denyReason })
      });

      if (response.ok) {
        toast.success("User denied");
        setShowDenyDialog(false);
        setSelectedUser(null);
        setDenyReason("");
        fetchUsers();
      }
    } catch (error) {
      toast.error("Failed to deny user");
    }
  };

  const handleDisable = async (targetUser) => {
    if (!window.confirm(`Disable ${targetUser.name}'s account? They will be logged out.`)) return;
    try {
      await fetch(`${API}/admin/users/${targetUser.user_id}/disable`, {
        method: "PUT",
        credentials: "include"
      });
      toast.success("User disabled");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to disable user");
    }
  };

  const handleEnable = async (targetUser) => {
    try {
      await fetch(`${API}/admin/users/${targetUser.user_id}/enable`, {
        method: "PUT",
        credentials: "include"
      });
      toast.success("User enabled");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to enable user");
    }
  };

  const handleRoleChange = async (targetUser, newRole) => {
    try {
      await fetch(`${API}/admin/users/${targetUser.user_id}/role`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ role: newRole })
      });
      toast.success("Role updated");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update role");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: "bg-[#F59E0B]/20", text: "text-[#F59E0B]", icon: Clock },
      active: { bg: "bg-[#22C55E]/20", text: "text-[#22C55E]", icon: CheckCircle2 },
      denied: { bg: "bg-[#EF4444]/20", text: "text-[#EF4444]", icon: XCircle },
      disabled: { bg: "bg-[#71717A]/20", text: "text-[#71717A]", icon: Ban }
    };
    const style = styles[status] || styles.pending;
    const Icon = style.icon;
    return (
      <span className={`badge ${style.bg} ${style.text} flex items-center gap-1`}>
        <Icon size={12} />
        {status}
      </span>
    );
  };

  const openApproveDialog = (u) => {
    setSelectedUser(u);
    setSelectedRole("artist");
    setShowApproveDialog(true);
  };

  const openDenyDialog = (u) => {
    setSelectedUser(u);
    setDenyReason("");
    setShowDenyDialog(true);
  };

  const openUserDetail = (u) => {
    setSelectedUser(u);
    setShowUserDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="user-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#FAFAFA] flex items-center gap-2">
            <Shield className="text-[#D4AF37]" size={28} />
            User Management
          </h1>
          <p className="text-[#A1A1AA] mt-1">Manage staff accounts and customer access</p>
        </div>
        <Button onClick={fetchUsers} variant="outline" className="border-[#27272A]">
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Pending Approvals Alert */}
      {pendingUsers.length > 0 && (
        <Card className="bg-[#F59E0B]/10 border-[#F59E0B]">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Clock size={24} className="text-[#F59E0B]" />
              <div>
                <p className="font-medium text-[#FAFAFA]">
                  {pendingUsers.length} Pending Approval{pendingUsers.length > 1 ? 's' : ''}
                </p>
                <p className="text-sm text-[#A1A1AA]">
                  Review and approve new staff members below
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList className="bg-[#121214] border border-[#27272A]">
          <TabsTrigger value="pending" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B]">
            Pending ({pendingUsers.length})
          </TabsTrigger>
          <TabsTrigger value="staff" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B]">
            All Staff ({allUsers.length})
          </TabsTrigger>
          <TabsTrigger value="customers" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B]">
            Customers ({customers.length})
          </TabsTrigger>
        </TabsList>

        {/* Pending Tab */}
        <TabsContent value="pending">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {pendingUsers.map((u) => (
              <Card key={u.user_id} className="bg-[#121214] border-[#27272A]" data-testid={`pending-user-${u.user_id}`}>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    {u.picture ? (
                      <img src={u.picture} alt={u.name} className="w-14 h-14 rounded-full border-2 border-[#F59E0B]" />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-[#F59E0B] flex items-center justify-center text-[#0A0A0B] font-bold text-xl">
                        {u.name?.charAt(0)}
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="font-semibold text-[#FAFAFA]">{u.name}</h3>
                      <p className="text-sm text-[#71717A]">{u.email}</p>
                      <p className="text-xs text-[#71717A] mt-1">
                        Signed up: {new Date(u.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button 
                      onClick={() => openApproveDialog(u)}
                      className="flex-1 btn-gold"
                      data-testid={`approve-${u.user_id}`}
                    >
                      <Check size={16} className="mr-1" />
                      Approve
                    </Button>
                    <Button 
                      onClick={() => openDenyDialog(u)}
                      variant="outline"
                      className="flex-1 border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10"
                    >
                      <X size={16} className="mr-1" />
                      Deny
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {pendingUsers.length === 0 && (
              <div className="col-span-full text-center py-12">
                <CheckCircle2 size={48} className="text-[#22C55E] mx-auto mb-3" />
                <p className="text-[#A1A1AA]">No pending approvals</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* All Staff Tab */}
        <TabsContent value="staff">
          <Card className="bg-[#121214] border-[#27272A]">
            <CardContent className="pt-6">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(allUsers || []).map((u) => (
                    <tr 
                      key={u.user_id} 
                      className="cursor-pointer hover:bg-[#1A1A1D]/50 transition-colors"
                      onClick={() => openUserDetail(u)}
                    >
                      <td>
                        <div className="flex items-center gap-3">
                          {u.picture ? (
                            <img src={u.picture} alt={u.name} className="w-8 h-8 rounded-full" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-[#D4AF37] flex items-center justify-center text-[#0A0A0B] font-bold text-sm">
                              {u.name?.charAt(0)}
                            </div>
                          )}
                          <span className="text-[#FAFAFA]">{u.name}</span>
                        </div>
                      </td>
                      <td className="text-[#A1A1AA]">{u.email}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {u.status === "active" && u.user_id !== user.user_id ? (
                          <Select
                            value={u.role}
                            onValueChange={(v) => handleRoleChange(u, v)}
                          >
                            <SelectTrigger className="w-36 h-8 text-xs bg-[#1A1A1D] border-[#27272A]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#1A1A1D] border-[#27272A]">
                              {staffRoles.map((role) => (
                                <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-[#D4AF37] text-sm">
                            {staffRoles.find(r => r.value === u.role)?.label || u.role}
                          </span>
                        )}
                      </td>
                      <td>{getStatusBadge(u.status)}</td>
                      <td>
                        {u.user_id !== user.user_id && (
                          <>
                            {u.status === "active" ? (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleDisable(u)}
                                className="text-[#EF4444] hover:bg-[#EF4444]/10"
                              >
                                <Ban size={14} className="mr-1" />
                                Disable
                              </Button>
                            ) : u.status === "disabled" ? (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleEnable(u)}
                                className="text-[#22C55E] hover:bg-[#22C55E]/10"
                              >
                                <CheckCircle2 size={14} className="mr-1" />
                                Enable
                              </Button>
                            ) : null}
                          </>
                        )}
                        {u.user_id === user.user_id && (
                          <span className="text-xs text-[#71717A]">You</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Customers Tab */}
        <TabsContent value="customers">
          <Card className="bg-[#121214] border-[#27272A]">
            <CardContent className="pt-6">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((c) => (
                    <tr key={c.user_id}>
                      <td>
                        <div className="flex items-center gap-3">
                          {c.picture ? (
                            <img src={c.picture} alt={c.name} className="w-8 h-8 rounded-full" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-[#3B82F6] flex items-center justify-center text-white font-bold text-sm">
                              {c.name?.charAt(0)}
                            </div>
                          )}
                          <span className="text-[#FAFAFA]">{c.name}</span>
                        </div>
                      </td>
                      <td className="text-[#A1A1AA]">{c.email}</td>
                      <td>{getStatusBadge(c.status)}</td>
                      <td className="text-[#71717A]">
                        {new Date(c.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                  {customers.length === 0 && (
                    <tr>
                      <td colSpan="4" className="text-center text-[#71717A] py-8">
                        No customers yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Approve Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent className="bg-[#121214] border-[#27272A]">
          <DialogHeader>
            <DialogTitle className="text-[#FAFAFA]">Approve Staff Member</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center gap-4 p-4 bg-[#1A1A1D] rounded-lg">
                {selectedUser.picture ? (
                  <img src={selectedUser.picture} alt={selectedUser.name} className="w-12 h-12 rounded-full" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37] flex items-center justify-center text-[#0A0A0B] font-bold">
                    {selectedUser.name?.charAt(0)}
                  </div>
                )}
                <div>
                  <p className="font-medium text-[#FAFAFA]">{selectedUser.name}</p>
                  <p className="text-sm text-[#71717A]">{selectedUser.email}</p>
                </div>
              </div>

              <div>
                <Label className="text-[#A1A1AA]">Assign Role</Label>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1D] border-[#27272A]">
                    {staffRoles.map((role) => (
                      <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button onClick={handleApprove} className="w-full btn-gold">
                <Check size={18} className="mr-2" />
                Approve as {staffRoles.find(r => r.value === selectedRole)?.label}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Deny Dialog */}
      <Dialog open={showDenyDialog} onOpenChange={setShowDenyDialog}>
        <DialogContent className="bg-[#121214] border-[#27272A]">
          <DialogHeader>
            <DialogTitle className="text-[#FAFAFA]">Deny Access</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center gap-4 p-4 bg-[#1A1A1D] rounded-lg">
                {selectedUser.picture ? (
                  <img src={selectedUser.picture} alt={selectedUser.name} className="w-12 h-12 rounded-full" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-[#EF4444] flex items-center justify-center text-white font-bold">
                    {selectedUser.name?.charAt(0)}
                  </div>
                )}
                <div>
                  <p className="font-medium text-[#FAFAFA]">{selectedUser.name}</p>
                  <p className="text-sm text-[#71717A]">{selectedUser.email}</p>
                </div>
              </div>

              <div>
                <Label className="text-[#A1A1AA]">Reason (Optional)</Label>
                <Textarea
                  value={denyReason}
                  onChange={(e) => setDenyReason(e.target.value)}
                  placeholder="Provide a reason for denial..."
                  className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                />
              </div>

              <Button onClick={handleDeny} className="w-full bg-[#EF4444] hover:bg-[#DC2626] text-white">
                <X size={18} className="mr-2" />
                Deny Access
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* User Detail Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent className="bg-[#121214] border-[#27272A] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#FAFAFA]">User Details</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center gap-4 p-4 bg-[#1A1A1D] rounded-lg">
                {selectedUser.picture ? (
                  <img src={selectedUser.picture} alt={selectedUser.name} className="w-16 h-16 rounded-full border-2 border-[#D4AF37]" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-[#D4AF37] flex items-center justify-center text-[#0A0A0B] font-bold text-2xl">
                    {selectedUser.name?.charAt(0)}
                  </div>
                )}
                <div>
                  <p className="font-semibold text-lg text-[#FAFAFA]">{selectedUser.name}</p>
                  <p className="text-sm text-[#A1A1AA]">{selectedUser.email}</p>
                  {getStatusBadge(selectedUser.status)}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-[#1A1A1D] rounded-lg">
                  <p className="text-xs text-[#71717A]">Role</p>
                  <p className="text-[#FAFAFA] capitalize">{selectedUser.role || "N/A"}</p>
                </div>
                <div className="p-3 bg-[#1A1A1D] rounded-lg">
                  <p className="text-xs text-[#71717A]">User Type</p>
                  <p className="text-[#FAFAFA] capitalize">{selectedUser.user_type || "N/A"}</p>
                </div>
                <div className="p-3 bg-[#1A1A1D] rounded-lg">
                  <p className="text-xs text-[#71717A]">Created</p>
                  <p className="text-[#FAFAFA]">{selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleDateString() : "N/A"}</p>
                </div>
                <div className="p-3 bg-[#1A1A1D] rounded-lg">
                  <p className="text-xs text-[#71717A]">User ID</p>
                  <p className="text-[#FAFAFA] text-xs truncate">{selectedUser.user_id}</p>
                </div>
              </div>

              {selectedUser.status === "pending" && (
                <div className="flex gap-2">
                  <Button 
                    onClick={() => { setShowUserDialog(false); openApproveDialog(selectedUser); }}
                    className="flex-1 btn-gold"
                  >
                    <Check size={16} className="mr-1" /> Approve
                  </Button>
                  <Button 
                    onClick={() => { setShowUserDialog(false); openDenyDialog(selectedUser); }}
                    variant="outline"
                    className="flex-1 border-[#EF4444] text-[#EF4444]"
                  >
                    <X size={16} className="mr-1" /> Deny
                  </Button>
                </div>
              )}

              {selectedUser.status === "active" && selectedUser.user_id !== user.user_id && (
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Label className="text-xs text-[#71717A]">Change Role</Label>
                    <Select 
                      value={selectedUser.role} 
                      onValueChange={(v) => { handleRoleChange(selectedUser, v); setShowUserDialog(false); }}
                    >
                      <SelectTrigger className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1D] border-[#27272A]">
                        {staffRoles.map((role) => (
                          <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button 
                    onClick={() => { handleDisable(selectedUser); setShowUserDialog(false); }}
                    variant="outline"
                    className="border-[#EF4444] text-[#EF4444] mt-auto"
                  >
                    <Ban size={16} className="mr-1" /> Disable
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
